import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { Home, CheckCircle, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import axios from '../api/axios';
import { useInvitations } from '../contexts/InvitationsContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { formatDate } from '../utils/format';

interface Invitation {
  id: string;
  email: string;
  role: 'host' | 'guest';
  status: 'pending' | 'accepted' | 'rejected';
  house: {
    id: string;
    name: string;
    description: string;
    address: string;
    host: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  createdAt: string;
}

export const InvitationsPage: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const { refreshPendingInvitations } = useInvitations();
  const { refreshNotificationCount } = useNotifications();

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/invitations');
        setInvitations(response.data);
        await refreshPendingInvitations();
        await refreshNotificationCount();
      } catch (err: any) {
        setError(err.response?.data?.message || t('invitations.failedFetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchInvitations();
  }, [refreshPendingInvitations, t]);

  const handleAccept = async (invitationId: string) => {
    try {
      await axios.post(`/invitations/${invitationId}/accept`);
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
      await refreshPendingInvitations();
      await refreshNotificationCount();
    } catch (err: any) {
      setError(err.response?.data?.message || t('invitations.failedAccept'));
    }
  };

  const handleReject = async (invitationId: string) => {
    try {
      await axios.post(`/invitations/${invitationId}/reject`);
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
      await refreshPendingInvitations();
      await refreshNotificationCount();
    } catch (err: any) {
      setError(err.response?.data?.message || t('invitations.failedReject'));
    }
  };

  return (
    <Layout title={t('invitations.layoutTitle')}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1">
            {t('invitations.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('invitations.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : invitations.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Home sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('invitations.empty')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('invitations.emptyDescription')}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {invitations.map((invitation) => (
              <Grid item xs={12} md={6} key={invitation.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{invitation.house.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={t(`common.roles.${invitation.role}`)} variant="outlined" size="small" />
                        <Chip label={t('common.statuses.pending')} color="warning" size="small" />
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {invitation.house.description}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>{t('invitations.location')}:</strong> {invitation.house.address}
                    </Typography>

                    {invitation.house.host && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t('common.labels.host')}:</strong> {invitation.house.host.firstName} {invitation.house.host.lastName}
                      </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>{t('invitations.invitedAs')}:</strong> {t(`common.roles.${invitation.role}`)}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      {t('common.labels.invitedOn', { date: formatDate(invitation.createdAt, i18n.resolvedLanguage) })}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => handleReject(invitation.id)}
                    >
                      {t('common.actions.decline')}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircle />}
                      onClick={() => handleAccept(invitation.id)}
                    >
                      {t('common.actions.accept')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Layout>
  );
};

export default InvitationsPage;
